import { useCallback, useEffect, useState } from "react";
import type { AuthUser } from "aws-amplify/auth";
import { tokenManager } from "@libs/token-manager";
import type { UserTokens } from "types";

const getTokens = async (forceRefresh: boolean): Promise<UserTokens | null> => {
  let accessToken;

  try {
    accessToken = await tokenManager.fetchAccessToken(forceRefresh);
  } catch (e) {
    console.log(e);
  }

  return accessToken ? { accessToken } : null;
};

export const useFetchTokens = (
  user: AuthUser,
): { userTokens: UserTokens | null; isLoading: boolean; refetch: VoidFunction } => {
  const [userTokens, setUserTokens] = useState<UserTokens | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(true);
  const [refetchIndex, setRefetchIndex] = useState(0);

  const refetch = useCallback(() => {
    setRefetchIndex(prevRefetchIndex => prevRefetchIndex + 1);
  }, []);

  const syncTokens = (forceRefresh = false) => {
    setIsLoading(true);

    getTokens(forceRefresh)
      .then(tokens => {
        setUserTokens(tokens);
      })
      .finally(() => {
        setIsLoading(false);
        setIsProcessing(false);
      });
  };

  useEffect(() => {
    syncTokens();
  }, [user]);

  useEffect(() => {
    if (refetchIndex > 0) {
      syncTokens(true);
    }
  }, [refetchIndex]);

  return { userTokens, isLoading: isLoading || isProcessing, refetch };
};
